'use client'

import { cn } from '@brew/ui/cn'
import dynamic from 'next/dynamic'
import { PROMPT_INPUT_PLAIN_TEXTAREA_CLASSNAME } from '@/components/ai-elements/prompt-input-metrics'
import { COMPOSER_PLACEHOLDER_DEFAULT } from '@/components/chat/composer-brand-gate'
import { PANEL_HEADER_ROW_CLASS } from '@/components/global-chat/chat-panel-header-row'
import { AppHeaderBar } from '@/components/header/app-header-bar'
import { InputGroupTextarea } from '@/components/ui/input-group'
import { CANVAS_BOARD_BG_CLASS } from '@/lib/canvas/engine/overlay-host'
import {
  DEMO_CHAT_COLUMN_CLASS,
  DEMO_COMPOSER_SLOT_CLASS,
  DEMO_FRAME_CLASS,
  DemoComposerShell,
} from './demo-layout'

/**
 * Static stand-in with the demo's frame while its chunk loads: the chat
 * column (header row, the composer's shell at the bottom) beside the app
 * header's row over an empty board, so nothing shifts when the demo
 * mounts. No editor, no parser, no chat code. Exported for a host that
 * renders it on the server and decides on the client whether to load the
 * demo at all (the home hero, desktop only).
 */
export function ProductDemoPoster() {
  return (
    <div
      aria-hidden="true"
      inert
      className={DEMO_FRAME_CLASS}
      data-product-demo-poster=""
    >
      <div className={DEMO_CHAT_COLUMN_CLASS}>
        <div className={PANEL_HEADER_ROW_CLASS} />
        <div className={cn('mt-auto', DEMO_COMPOSER_SLOT_CLASS)}>
          <DemoComposerShell
            textarea={
              <InputGroupTextarea
                readOnly
                className={PROMPT_INPUT_PLAIN_TEXTAREA_CLASSNAME}
                placeholder={COMPOSER_PLACEHOLDER_DEFAULT}
              />
            }
          />
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <AppHeaderBar />
        <div className={cn('min-h-0 flex-1', CANVAS_BOARD_BG_CLASS)} />
      </div>
    </div>
  )
}

/**
 * The only entry to the product demo. The demo ships TipTap, the React
 * Email editor and the JSX parser, so it loads as its own chunk, client
 * side only — never in a page's initial JS (pinned by
 * `tests/dynamic-import-boundaries.test.ts`).
 */
export const LazyProductDemo = dynamic(
  () => import('./product-demo').then((module) => module.ProductDemo),
  { ssr: false, loading: ProductDemoPoster }
)
